/**
 * W1 „Ende vor der ersten Rechnung" (Workshop-Lücken 29.10.2026, Plan `.claude/plans/2026-10-29-workshop-luecken.md`
 * Paket U): zweite Grafik der Karte „Was aus den Anfos eines Quartals geworden ist". Je reifem Bestellquartal ein Balken
 * vom 25. bis 75. Perzentil des Tags, an dem die vor der ersten Rechnung beendeten Anfos endeten, Punkt am Median, Strich
 * beim 90. Perzentil; senkrechte Linie „Ende Testphase" nur, wenn das Backend sie freigibt (≥ 95 % gleiche Testphase).
 * Reines HTML (Prozent-Positionen) statt Nivo: schmal bis 390 px ohne Querscrollen, keine Animation.
 */
import { FC } from 'react';
import { Box, Tooltip } from '@mui/material';
import InfoTip from '../../../components/InfoTip';
import { int } from '../../../components/auswertungen/kennzahlTeile';
import { Legende, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatPeriode } from '../../../helpers/auswertungenData';
import { TERMS } from '../../../helpers/kpiDefinitions';
import type { AbbruchQuartal, AbbruchTage } from '../../../helpers/umsatzData';

const tage = (v: number | null): string => (v === null ? '–' : `${v.toLocaleString('de-DE', { maximumFractionDigits: 1 })}`);

/** Achsenende: nächstes Vielfaches von 10 über dem größten 90. Perzentil, mindestens 40 (Linie Tag 30 sichtbar). */
export function achsenEnde(qs: Pick<AbbruchQuartal, 'p90'>[]): number {
  const max = Math.max(40, ...qs.map((q) => q.p90 ?? 0));
  return Math.ceil((max + 1) / 10) * 10;
}

const AbbruchTageGrafik: FC<{ daten: AbbruchTage }> = ({ daten }) => {
  const viz = useVizStyle();
  const reif = daten.quartale.filter((q) => q.reif && q.median !== null);
  const jung = daten.quartale.filter((q) => !q.reif);
  if (reif.length === 0) return null;
  const ende = achsenEnde(reif);
  const x = (t: number) => `${Math.max(0, Math.min(100, (t / ende) * 100))}%`;
  const ticks = Array.from({ length: ende / 10 + 1 }, (_, i) => i * 10);
  const linie = daten.testphase_linie;
  const andereLaengen = linie === null ? reif.filter((q) => (q.testphase_30_anteil ?? 0) < daten.testphase_linie_anteil) : [];
  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ fontWeight: 600, fontSize: 14, mb: 0.5, display: 'flex', alignItems: 'center' }}>
        Wann die Anfos enden, die vor der ersten Rechnung aufhören
        <InfoTip text={TERMS.abbruchTag.tip} />
      </Box>
      <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1, maxWidth: '68ch', lineHeight: 1.5 }}>
        Je reifem Bestellquartal: an welchem Tag nach der Bestellung die Anfos der Stufe „{TERMS.stufeVorErsterRechnung.short}“ endeten.
        {linie !== null ? ` Die senkrechte Linie ist das Ende der Testphase (Tag ${linie}).` : ''}
      </Box>
      <Legende
        items={[
          { label: TERMS.abbruchSpanne.label, color: viz.series1Soft },
          { label: 'Median', color: viz.series1 },
          { label: '90. Perzentil', color: viz.inkSecondary, shape: 'line' },
          ...(linie !== null ? [{ label: `${TERMS.endeTestphase.label} (Tag ${linie})`, color: viz.series2, shape: 'line' as const }] : []),
        ]}
      />
      {/* Schmal (390 px) blieben mit 72 + 78 px Randspalten nur ~100 px für 0–50 Tage — Ränder dort enger. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 54px) minmax(0, 1fr) minmax(0, 54px)', sm: 'minmax(0, 72px) minmax(0, 1fr) minmax(0, 78px)' },
          columnGap: 1,
          rowGap: 0.75,
          alignItems: 'center',
        }}
      >
        {reif.map((q) => (
          <Box key={q.quarter} sx={{ display: 'contents' }}>
            <Box sx={{ fontSize: { xs: 12, sm: 13 }, whiteSpace: 'nowrap' }}>{formatPeriode(q.quarter)}</Box>
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                  <strong>{formatPeriode(q.quarter)}</strong>: {int(q.n)} Anfos vor der ersten Rechnung beendet
                  <br />
                  Median Tag {tage(q.median)} · mittlere Hälfte Tag {tage(q.p25)}–{tage(q.p75)} · 90. Perzentil Tag {tage(q.p90)} · spätestens Tag {tage(q.max)}
                  {q.ohne_datum > 0 && (
                    <>
                      <br />
                      {int(q.ohne_datum)} ohne Kündigungsdatum (nicht in der Grafik)
                    </>
                  )}
                </Box>
              }
            >
              <Box sx={{ position: 'relative', height: 22, cursor: 'help' }} aria-label={`${formatPeriode(q.quarter)}: Median Tag ${tage(q.median)}`}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, top: 10, height: '1px', backgroundColor: viz.grid }} />
                {linie !== null && <Box sx={{ position: 'absolute', left: x(linie), top: -3, bottom: -3, width: '2px', ml: '-1px', backgroundColor: viz.series2 }} />}
                <Box
                  sx={{
                    position: 'absolute',
                    left: x(q.p25 as number),
                    width: `calc(${x(q.p75 as number)} - ${x(q.p25 as number)})`,
                    top: 5,
                    height: 12,
                    borderRadius: '3px',
                    backgroundColor: viz.series1Soft,
                  }}
                />
                <Box sx={{ position: 'absolute', left: x(q.p90 as number), top: 3, height: 16, width: '2px', ml: '-1px', backgroundColor: viz.inkSecondary }} />
                <Box
                  sx={{
                    position: 'absolute',
                    left: x(q.median as number),
                    top: 6,
                    width: 10,
                    height: 10,
                    ml: '-5px',
                    borderRadius: '50%',
                    backgroundColor: viz.series1,
                    border: '1px solid',
                    borderColor: 'background.paper',
                  }}
                />
              </Box>
            </Tooltip>
            <Box sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'right', lineHeight: 1.25 }}>
              Tag {tage(q.median)}
              <Box component="span" sx={{ display: 'block', fontSize: 11 }}>
                {int(q.n)} Anfos
              </Box>
            </Box>
          </Box>
        ))}
        <Box />
        <Box sx={{ position: 'relative', height: 16, fontSize: 11, color: 'text.secondary' }}>
          {ticks.map((t) => (
            <Box key={t} component="span" sx={{ position: 'absolute', left: x(t), transform: t === 0 ? 'none' : t === ende ? 'translateX(-100%)' : 'translateX(-50%)' }}>
              {t}
            </Box>
          ))}
        </Box>
        <Box sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'right' }}>Tage</Box>
      </Box>
      <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 1, maxWidth: '75ch', lineHeight: 1.5 }}>
        Gezählt sind genau die Anfos der Stufe „{TERMS.stufeVorErsterRechnung.short}“ (Tabelle oben). Gemessen ist das Kündigungsdatum laut PL-X,
        nicht zwingend ein Ende in der Testphase.
        {jung.length > 0 && ` Junge Quartale (${jung.map((q) => formatPeriode(q.quarter)).join(', ')}) fehlen: dort enden noch Anfos.`}
        {linie === null &&
          andereLaengen.length > 0 &&
          ` Keine Linie „${TERMS.endeTestphase.short}“: ${andereLaengen
            .map((q) => `${formatPeriode(q.quarter)} ${q.testphase.map((t) => `${t.tage === null ? 'unbekannt' : `${t.tage} T.`} ${int(t.n)}`).join(', ')}`)
            .join('; ')}.`}
      </Box>
    </Box>
  );
};

export default AbbruchTageGrafik;
